// @flow

import React from 'react';
import SV from 'server-vars';
import { localStorage } from 'dibs-browser-storage';
import getter from 'dibs-object-getter';
import LoginForm from './DealerLogin';
import LoginMessage from './LoginMessage';
import CookieDisclaimer from './CookieDisclaimer';
import formCopy from '../../../../../copy/loginCopy.es';
import { addGTMMessage } from 'dibs-tracking/tracking';

type LOGIN_VIEW_TYPES = 'login' | 'resetPasswordEmail' | 'resetPassword';

type LoginProps = {
    formType: string,
};

type IntlCopy = {
    defaultMessage?: string,
    id: string,
};

type LoginState = {
    cookieDisclaimerDismissed: boolean,
    errors: {
        email: IntlCopy,
        password: IntlCopy,
        password1: IntlCopy,
        vertical: IntlCopy,
    },
    tooltipIsVisible: boolean,
    values: {
        email: string,
        password: string,
        password1: string,
        passwordConfirmationSet: boolean,
        vertical: string,
    },
};

const RESET_NEW_FORM = 'resetPasswordEmail';
const RESET_EXPIRED = 'resetExpired';

const hasSpecialFieldValidations = (field: string): boolean => {
    return ['email', 'password', 'password1'].includes(field);
};

const viewFields = {
    login: ['email', 'password'],
    resetPassword: ['password', 'password1'],
    resetPasswordEmail: ['email', 'vertical'],
};

class Login extends React.Component<LoginProps, LoginState> {
    constructor(props: LoginProps) {
        super(props);
        this.state = {
            cookieDisclaimerDismissed: false,
            errors: {
                email: formCopy.validations.noError,
                password: formCopy.validations.noError,
                password1: formCopy.validations.noError,
                vertical: formCopy.validations.noError,
            },
            tooltipIsVisible: false,
            values: {
                email: '',
                password: '',
                password1: '',
                passwordConfirmationSet: false,
                vertical: '',
            },
        };
        this.handleTooltipClick = this.handleTooltipClick.bind(this);
        this.handleVerticalSelection = this.handleVerticalSelection.bind(this);
        this.handleChange = this.handleChange.bind(this);
        this.handleFieldBlur = this.handleFieldBlur.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    componentDidMount() {
        addGTMMessage({ event: 'gtm_pageview' });
    }

    closeCookieDisclaimer = (): mixed => {
        localStorage.setItem('showCookieDisclaimer', false);
        this.setState({ cookieDisclaimerDismissed: true });
    };

    validateEmailPassword = (field: string, value: string) => {
        const validEmail = /.+@.+\..+/i;
        const passwordStrength = /^(?=.*\d)(?=.*[A-Z]).{8,}$/;
        const errors = { ...this.state.errors };
        const values = { ...this.state.values };
        switch (field) {
            case 'email':
                errors.email = validEmail.test(value)
                    ? formCopy.validations.noError
                    : formCopy.validations.validEmail;
                this.setState({ errors });
                break;
            case 'password':
                if (this.state.values.passwordConfirmationSet) {
                    errors.password1 =
                        this.state.values.password === this.state.values.password1
                            ? formCopy.validations.noError
                            : formCopy.validations.matching;
                }
                errors.password = passwordStrength.test(value)
                    ? formCopy.validations.noError
                    : formCopy.validations.strength;
                this.setState({ errors });
                break;
            case 'password1':
                errors.password1 =
                    this.state.values.password === value
                        ? formCopy.validations.noError
                        : formCopy.validations.matching;
                values.passwordConfirmationSet = true;
                this.setState({
                    errors,
                    values,
                });
                break;
        }
    };
    handleTooltipClick = (): mixed => {
        const tooltipIsVisible = !this.state.tooltipIsVisible;
        this.setState({ tooltipIsVisible });
    };
    handleVerticalSelection = (selection: SyntheticInputEvent<HTMLSelectElement>): void => {
        const selectionValue = getter(selection.target).value('value');
        const values = { ...this.state.values };
        const errors = { ...this.state.errors };
        if (this.state.values.vertical !== selectionValue) {
            values.vertical = selectionValue || '';
            errors.vertical = selectionValue
                ? formCopy.validations.noError
                : formCopy.validations.required;
            this.setState({ errors, values });
        }
    };
    handleChange = (e: Object, field: string) => {
        const fieldValue = (e && e.target && e.target.value) || '';
        const values = { ...this.state.values };

        values[field] = fieldValue;
        this.setState({ values });
    };
    handleFieldBlur = (e: Object, field: string, formType?: string): void => {
        const fieldValue = e && e.target && e.target.value;
        const errors = { ...this.state.errors };
        const values = { ...this.state.values };

        if (!fieldValue) {
            errors[field] = formCopy.validations.required;
            values[field] = '';
            this.setState({ errors, values });
        } else {
            values[field] = fieldValue;
            errors[field] = formCopy.validations.noError;
            this.setState({ errors, values });

            if (hasSpecialFieldValidations(field) && formType !== 'login') {
                this.setState({ values }, () => {
                    this.validateEmailPassword(field, fieldValue);
                });
            }
        }
    };
    handleSubmit = (e: Object, formType: LOGIN_VIEW_TYPES): boolean => {
        const errors = { ...this.state.errors };
        viewFields[formType].forEach(field => {
            if (!this.state.values[field]) {
                errors[field] = formCopy.validations.required;
            }
        });
        this.setState({ errors });
        const invalidForm = viewFields[formType].some(field => !!errors[field].defaultMessage);

        if (invalidForm) {
            e.preventDefault();
            return false;
        }
        return true;
    };

    render() {
        const errorMessage = SV.get('login.errorMessage');
        const successMessage = SV.get('login.message');
        const messageType = SV.get('login.messageType');
        const action = SV.get('login.action') || '';
        const resetLink = SV.get('login.forgotPasswordLink') || '';
        const nonUSBasedRequest = SV.get('login.displayCookieDisclaimer');
        const hasNotDismissedCookieDisclaimer =
            localStorage.getItem('showCookieDisclaimer') === undefined
                ? true
                : localStorage.getItem('showCookieDisclaimer');

        const formType = messageType === RESET_EXPIRED ? RESET_NEW_FORM : this.props.formType;

        return (
            // $FlowFixMe
            <div>
                <LoginMessage
                    errorMessage={errorMessage}
                    messageType={messageType}
                    successMessage={successMessage}
                />
                <LoginForm
                    action={action}
                    errors={this.state.errors}
                    resetLink={resetLink}
                    formType={formType}
                    handleFieldBlur={this.handleFieldBlur}
                    handleChange={this.handleChange}
                    handleSubmit={this.handleSubmit}
                    handleTooltipClick={this.handleTooltipClick}
                    handleVerticalSelection={this.handleVerticalSelection}
                    resetVertical={this.state.values.vertical}
                    tooltipIsVisible={this.state.tooltipIsVisible}
                />
                <CookieDisclaimer
                    shouldDisplayCookieDisclaimer={
                        !!(nonUSBasedRequest && hasNotDismissedCookieDisclaimer)
                    }
                    dismissDisclaimer={this.closeCookieDisclaimer}
                />
            </div>
        );
    }
}

export default Login;
